import React, { useEffect, useRef, useState } from 'react';
import cytoscape, { NodeSingular, EdgeSingular, Collection } from 'cytoscape';
import { Project, Station, Route } from '../../types';
import { EdgeContextMenu } from './EdgeContextMenu';
import { NodeContextMenu } from './NodeContextMenu';
import { UndoRedoControls } from './UndoRedoControls';
import { UndoManager, HistoryAction } from '../../lib/history/UndoManager';
import { InfoScreen } from './InfoScreen';
import { RoutesMenu } from './RoutesMenu';
import { CitiesMenu } from '../cities/CitiesMenu';
import { ConfigForm } from '../config/ConfigForm';
import { ProjectStore } from '../../lib/storage/projects';
import { parseGraphML } from '../../lib/import/graphml';
import { updateStationPopulation, getEffectivePopulation } from '../../utils/population';
import { getEffectiveDistance, getEdgeLabel, updateAutomaticDistancesForStation } from '../../utils/distance';
import { exportProject } from '../../lib/export/files';
import { calculateTotalRidership, formatNumber } from '../../utils/ridership';
import { calculateNetIncomePercentage, formatPercentage, getPercentageColor, calculateRevenue, calculateNetIncome } from '../../utils/finance';
import { StationCreationServiceImpl } from '../../services/StationCreationService';
import { formatCurrencyCompact } from '../../lib/format/currency';
import { URLManager } from '../../lib/url/URLManager';
import { closeMenuAndUpdateUrl } from '../../lib/url/menuUtils';

interface EditorProps {
  project: Project;
  onProjectChange: (project: Project, updateTimestamp?: boolean) => void;
  onCytoscapeInstance: (cy: cytoscape.Core) => void;
  isExportMode?: boolean;
  className?: string;
  initialSubView?: string | null;
}

interface SelectionState {
  selectedNodes: NodeSingular[];
  routeCreationSource: NodeSingular | null;
}

interface ViewState {
  zoom: number;
  pan: { x: number; y: number };
}

interface MovedNode {
  stationId: string;
  previousPosition: { x: number; y: number };
  newPosition: { x: number; y: number };
}

const MAX_STATIONS = 250; // Maximum number of stations allowed

const saveProject = (project: Project) => {
  try {
    localStorage.setItem(`project-${project.id}`, JSON.stringify(project));
    console.log('Project saved successfully:', project.id);
  } catch (error) {
    console.error('Failed to save project to local storage:', error);
  }
};

const getNextRouteId = (routes: Route[]): string => {
  const existingIds = routes.map(r => parseInt(r.id.split('-')[1])).filter(id => !isNaN(id));
  const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
  return `route-${maxId + 1}`;
};

export const NetworkEditor: React.FC<EditorProps> = ({ project, onProjectChange, onCytoscapeInstance, isExportMode = false, className, initialSubView }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | undefined>(undefined);
  const currentProjectRef = useRef(project);
  const [previousProjectId, setPreviousProjectId] = useState<string | null>(null);
  const stationCreationServiceRef = useRef<StationCreationServiceImpl | null>(null);
  const selectionStateRef = useRef<SelectionState>({
    selectedNodes: [],
    routeCreationSource: null
  });
  const isSelectionUpdateRef = useRef(false);
  const [selectionState, setSelectionState] = useState<SelectionState>({
    selectedNodes: [],
    routeCreationSource: null
  });
  const undoManagerRef = useRef<UndoManager | null>(null);
  const dragStartPositionRef = useRef<Array<{ id: string; x: number; y: number }> | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    route: Route;
    position: { x: number; y: number };
  } | null>(null);
  const [nodeContextMenu, setNodeContextMenu] = useState<{
    station: Station;
    position: { x: number; y: number };
  } | null>(null);
  const [showInfoScreen, setShowInfoScreen] = useState(false);
  const [showRoutesMenu, setShowRoutesMenu] = useState(false);
  const [showCitiesMenu, setShowCitiesMenu] = useState(false);
  const [showConfigForm, setShowConfigForm] = useState(false);

  // Add this new function near the top of the component, after other utility functions
  const updateNodeLabel = (node: NodeSingular, name: string) => {
    node.data('name', name);
    node.data('label', name);
  };

  // Add this helper function near the top of the component
  const calculateMenuPosition = (node: cytoscape.NodeSingular) => {
    const cy = cyRef.current;
    if (!cy) return { x: 0, y: 0 };

    // Get the node's rendered position (accounts for zoom and pan)
    const renderedPosition = node.renderedPosition();
    
    // Always position menu 40px to the right of the node's center
    return { 
      x: renderedPosition.x + 40,
      y: renderedPosition.y
    };
  };

  // Handle initial subview state
  useEffect(() => {
    if (initialSubView) {
      switch (initialSubView) {
        case 'cities':
          setShowCitiesMenu(true);
          break;
        case 'routes':
          setShowRoutesMenu(true);
          break;
        case 'help':
          setShowInfoScreen(true);
          break;
        case 'editor':
          // Default view - show graph editor
          setShowCitiesMenu(false);
          setShowRoutesMenu(false);
          setShowInfoScreen(false);
          break;
        default:
          // If no valid view is specified, show graph editor
          setShowCitiesMenu(false);
          setShowRoutesMenu(false);
          setShowInfoScreen(false);
      }
    } else {
      // If no initialSubView is provided, show graph editor
      setShowCitiesMenu(false);
      setShowRoutesMenu(false);
      setShowInfoScreen(false);
    }
  }, [initialSubView]);

  // Handle menu closing
  const handleCloseCitiesMenu = () => {
    console.log('Closing cities menu, maintaining editor view');
    setShowCitiesMenu(false);
    closeMenuAndUpdateUrl(project.identifier);
  };

  const handleCloseRoutesMenu = () => {
    console.log('Closing routes menu, maintaining editor view');
    setShowRoutesMenu(false);
    closeMenuAndUpdateUrl(project.identifier);
  };

  const handleCloseInfoScreen = () => {
    console.log('Closing info screen, maintaining editor view');
    setShowInfoScreen(false);
    closeMenuAndUpdateUrl(project.identifier);
  };

  const handleCloseConfigForm = () => {
    console.log('Closing config form, maintaining editor view');
    setShowConfigForm(false);
    // Update URL to maintain editor view
    if (project.identifier) {
      const url = `/project/${project.identifier}/network/editor`;
      window.history.pushState({}, '', url);
    }
  };

  // Handle node selection
  const handleNodeSelection = (node: NodeSingular | null) => {
    if (!cyRef.current) return;

    // Update selection state
    const newSelectedNodes = node ? [node] : [];
    selectionStateRef.current = {
      selectedNodes: newSelectedNodes,
      routeCreationSource: node
    };
    setSelectionState(selectionStateRef.current);

    // Update visual selection
    cyRef.current.nodes().forEach(n => {
      if (node && n.id() === node.id()) {
        n.select();
      } else {
        n.unselect();
      }
    });
  };

  // Handle route creation
  const handleRouteCreation = (sourceNode: NodeSingular, targetNode: NodeSingular) => {
    const sourceId = sourceNode.id();
    const targetId = targetNode.id();

    console.log('Attempting route creation:', { 
      sourceId, 
      targetId,
      sourceNodeName: sourceNode.data('name'),
      targetNodeName: targetNode.data('name'),
      currentSelection: selectionStateRef.current.selectedNodes.map(n => n.data('name'))
    });

    // Check if edge already exists
    const existingEdge = cyRef.current?.edges().some(edge => {
      const edgeData = edge.data();
      const exists = (edgeData.source === sourceId && edgeData.target === targetId) ||
                   (edgeData.source === targetId && edgeData.target === sourceId);
      if (exists) {
        console.log('Route already exists between nodes:', {
          source: edgeData.source,
          target: edgeData.target
        });
      }
      return exists;
    });

    if (existingEdge) {
      console.log('Route already exists, skipping creation');
      return;
    }

    const newRoute: Route = {
      id: `route-${Date.now()}`,
      source: sourceId,
      target: targetId,
      distance: 0,
      landType: 'domestic',
      distanceCalculation: 'manual',
      automaticDistance: null
    };
    console.log('Created new route object:', newRoute);

    undoManagerRef.current?.addAction({
      type: 'ADD_ROUTE',
      data: {
        route: newRoute
      }
    });

    // Create updated project
    const updatedProject = {
      ...currentProjectRef.current,
      network: {
        ...currentProjectRef.current.network,
        routes: [...currentProjectRef.current.network.routes, newRoute]
      }
    };

    // Update refs and save in a single batch
    currentProjectRef.current = updatedProject;
    saveProject(updatedProject);

    // Add the edge to cytoscape
    console.log('Adding new edge to cytoscape:', {
      id: newRoute.id,
      source: sourceId,
      target: targetId
    });
    
    cyRef.current?.add({
      data: {
        id: newRoute.id,
        source: sourceId,
        target: targetId,
        route: newRoute,
        label: getEdgeLabel(newRoute, updatedProject),
        passengers: 0,
        passengersPerKm: 0
      }
    });
    
    // Update project state once
    onProjectChange(updatedProject);
    
    // Update undo/redo state
    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);

    console.log('Route creation complete');
  };

  // Initialize StationCreationService
  useEffect(() => {
    stationCreationServiceRef.current = new StationCreationServiceImpl(
      currentProjectRef.current,
      (updater) => {
        const updatedProject = updater(currentProjectRef.current);
        currentProjectRef.current = updatedProject;
        saveProject(updatedProject);
        onProjectChange(updatedProject);
      },
      cyRef
    );
  }, [project.id]); // Reinitialize when project changes

  // Keep currentProjectRef in sync with project props
  useEffect(() => {
    currentProjectRef.current = project;
    // Update the service with the latest project state
    if (stationCreationServiceRef.current) {
      stationCreationServiceRef.current = new StationCreationServiceImpl(
        project,
        (updater) => {
          const updatedProject = updater(project);
          currentProjectRef.current = updatedProject;
          saveProject(updatedProject);
          onProjectChange(updatedProject);
        },
        cyRef
      );
    }
  }, [project]);

  // Initialize undo manager
  useEffect(() => {
    if (!undoManagerRef.current && project.id) {
      undoManagerRef.current = new UndoManager(project.id);
      setCanUndo(undoManagerRef.current.canUndo());
      setCanRedo(undoManagerRef.current.canRedo());
    }
  }, [project.id]);

  // Update undo/redo state when project changes
  useEffect(() => {
    if (undoManagerRef.current) {
      const canUndoNow = undoManagerRef.current.canUndo();
      const canRedoNow = undoManagerRef.current.canRedo();
      if (canUndo !== canUndoNow) setCanUndo(canUndoNow);
      if (canRedo !== canRedoNow) setCanRedo(canRedoNow);
    }
  }, [project]);

  // Initialize cytoscape
  useEffect(() => {
    if (!containerRef.current) return;

    // Destroy existing instance if it exists
    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = undefined;
    }

    console.log('Initializing Cytoscape instance with project:', {
      projectId: project.id,
      stationCount: project.network.stations.length,
      routeCount: project.network.routes.length
    });

    cyRef.current = cytoscape({
      container: containerRef.current,
      elements: {
        nodes: project.network.stations.map(station => ({
          data: {
            id: station.id,
            label: station.name,
            name: station.name,
            station
          },
          position: station.position || { x: 0, y: 0 }
        })),
        edges: project.network.routes.map(route => ({
          data: {
            id: route.id,
            source: route.source,
            target: route.target,
            route,
            label: getEdgeLabel(route, project),
            passengers: project.matrices?.passengersBySegment?.[
              project.network.stations.findIndex(s => s.id === route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === route.target)
            ] || 0,
            passengersPerKm: project.matrices?.passengersBySegmentKm?.[
              project.network.stations.findIndex(s => s.id === route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === route.target)
            ] || 0
          }
        }))
      },
      style: [
        {
          selector: 'node[label]',  // Only apply to nodes with label property
          style: {
            'background-color': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              const population = station ? getEffectivePopulation(station) : 0;
              return population === 0 ? '#666666' : '#6B89B0';
            },
            'border-width': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              return station?.latitude && station?.longitude ? 0 : 1;
            },
            'border-color': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              return station?.latitude && station?.longitude ? '#666666' : '#000000';
            },
            'border-opacity': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              return station?.latitude && station?.longitude ? 0 : 1;
            },
            'label': 'data(label)',
            'width': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              const population = station ? getEffectivePopulation(station) : 0;
              const baseSize = 20;
              
              // Get all stations' populations, excluding zeros
              const allNodes = ele.cy().nodes();
              const populations = allNodes
                .map(node => {
                  const station = node.data('station');
                  return station ? getEffectivePopulation(station) : 0;
                })
                .filter(pop => pop > 0);
              
              // If no non-zero populations, return base size
              if (populations.length === 0) return `${baseSize}px`;
              
              const minPop = Math.min(...populations);
              const maxPop = Math.max(...populations);
              
              // If all non-zero populations are the same, return base size
              if (maxPop === minPop) return `${baseSize}px`;
              
              // Use logarithmic scaling for smoother transitions
              const logMin = Math.log(minPop + 1);
              const logMax = Math.log(maxPop + 1);
              const logPop = Math.log(population + 1);
              
              // Scale between 1x and 3x base size using log scale, clamped to prevent negative values
              const scale = Math.max(1, Math.min(3, 1 + ((logPop - logMin) / (logMax - logMin)) * 2));
              return `${baseSize * scale}px`;
            },
            'height': (ele: cytoscape.NodeSingular) => {
              const station = ele.data('station');
              const population = station ? getEffectivePopulation(station) : 0;
              const baseSize = 20;
              
              // Get all stations' populations, excluding zeros
              const allNodes = ele.cy().nodes();
              const populations = allNodes
                .map(node => {
                  const station = node.data('station');
                  return station ? getEffectivePopulation(station) : 0;
                })
                .filter(pop => pop > 0);
              
              // If no non-zero populations, return base size
              if (populations.length === 0) return `${baseSize}px`;
              
              const minPop = Math.min(...populations);
              const maxPop = Math.max(...populations);
              
              // If all non-zero populations are the same, return base size
              if (maxPop === minPop) return `${baseSize}px`;
              
              // Use logarithmic scaling for smoother transitions
              const logMin = Math.log(minPop + 1);
              const logMax = Math.log(maxPop + 1);
              const logPop = Math.log(population + 1);
              
              // Scale between 1x and 3x base size using log scale, clamped to prevent negative values
              const scale = Math.max(1, Math.min(3, 1 + ((logPop - logMin) / (logMax - logMin)) * 2));
              return `${baseSize * scale}px`;
            },
            'font-size': '12px',
            'font-weight': 'bold',
            'text-valign': 'top',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'color': '#333333',
            'background-opacity': 1,
            'text-background-color': '#ffffff',
            'text-background-opacity': 1,
            'text-background-padding': '6px',
            'text-background-shape': 'roundrectangle',
            'text-border-width': 1,
            'text-border-color': '#cccccc',
            'text-border-opacity': 1,
            'text-margin-y': -15,
            'text-events': 'yes'
          }
        },
        {
          selector: 'node:selected',
          style: {
            'background-color': '#dc6c3a',
            'border-width': 2,
            'border-color': '#dc6c3a',
            'border-opacity': 1
          }
        },
        {
          selector: 'edge',
          style: {
            'width': (ele: cytoscape.EdgeSingular) => {
              const passengers = ele.data('passengers');
              const route = ele.data('route');
              const baseWidth = 3;
              const maxWidth = 10;
              
              // If route has 0 distance or passengers is invalid, return base width
              if (getEffectiveDistance(route, currentProjectRef.current) === 0 || typeof passengers !== 'number' || isNaN(passengers)) {
                return baseWidth;
              }
              
              // Get all edges' passenger counts, filtering out invalid values
              const allEdges = ele.cy().edges();
              const passengerCounts = allEdges
                .map((edge: cytoscape.EdgeSingular) => {
                  const count = edge.data('passengers');
                  return typeof count === 'number' && !isNaN(count) ? count : 0;
                })
                .filter((count: number) => count > 0);
              
              // If no valid passenger counts, return base width
              if (passengerCounts.length === 0) return baseWidth;
              
              const minPassengers = Math.min(...passengerCounts);
              const maxPassengers = Math.max(...passengerCounts);
              
              // If all passenger counts are the same, return base width
              if (maxPassengers === minPassengers) return baseWidth;
              
              // Use linear scaling and ensure we get a positive value
              const scale = Math.max(0, Math.min(1, (passengers - minPassengers) / (maxPassengers - minPassengers)));
              return baseWidth + (maxWidth - baseWidth) * scale;
            },
            'line-color': (ele: cytoscape.EdgeSingular) => {
              const passengersPerKm = ele.data('passengersPerKm');
              if (typeof passengersPerKm !== 'number' || isNaN(passengersPerKm) || passengersPerKm === 0) {
                return '#666';
              }

              // Get all edges' passengers per km, excluding zeros
              const allEdges = ele.cy().edges();
              const passengersPerKmValues = allEdges
                .map((edge: cytoscape.EdgeSingular) => {
                  const value = edge.data('passengersPerKm');
                  return typeof value === 'number' && !isNaN(value) && value > 0 ? value : null;
                })
                .filter((value): value is number => value !== null);

              if (passengersPerKmValues.length === 0) return '#666';

              const minValue = Math.min(...passengersPerKmValues);
              const maxValue = Math.max(...passengersPerKmValues);

              if (maxValue === minValue) return '#666';

              // Calculate position in gradient (0 to 1)
              const gradientPosition = (passengersPerKm - minValue) / (maxValue - minValue);

              // Define gradient colors
              const highColor = '#A8E6A3';  // Green
              const midColor = '#4A90E2';   // Blue
              const lowColor = '#7D3C98';   // Purple

              // Convert hex to RGB
              const hexToRgb = (hex: string) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16)
                } : null;
              };

              const highRgb = hexToRgb(highColor)!;
              const midRgb = hexToRgb(midColor)!;
              const lowRgb = hexToRgb(lowColor)!;

              // Calculate color based on gradient position
              let r, g, b;
              if (gradientPosition < 0.5) {
                // Interpolate between low and mid
                const t = gradientPosition * 2;
                r = Math.max(0, Math.min(255, Math.round(lowRgb.r + (midRgb.r - lowRgb.r) * t)));
                g = Math.max(0, Math.min(255, Math.round(lowRgb.g + (midRgb.g - lowRgb.g) * t)));
                b = Math.max(0, Math.min(255, Math.round(lowRgb.b + (midRgb.b - lowRgb.b) * t)));
              } else {
                // Interpolate between mid and high
                const t = (gradientPosition - 0.5) * 2;
                r = Math.max(0, Math.min(255, Math.round(midRgb.r + (highRgb.r - midRgb.r) * t)));
                g = Math.max(0, Math.min(255, Math.round(midRgb.g + (highRgb.g - midRgb.g) * t)));
                b = Math.max(0, Math.min(255, Math.round(midRgb.b + (highRgb.b - midRgb.b) * t)));
              }

              return `rgb(${r}, ${g}, ${b})`;
            },
            'line-style': (ele) => {
              const route = ele.data('route') as Route;
              switch (route.landType) {
                case 'customs': return 'dashed';
                case 'no-customs': return 'dotted';
                default: return 'solid';
              }
            },
            'curve-style': 'straight',
            'label': 'data(label)',
            'text-rotation': 'autorotate',
            'text-margin-y': 0,
            'font-size': '10px',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '80px',
            'color': '#333333',
            'text-background-color': '#ffffff',
            'text-background-opacity': 1,
            'text-background-padding': '4px',
            'text-background-shape': 'roundrectangle',
            'text-border-width': 1,
            'text-border-color': '#cccccc',
            'text-border-opacity': 1
          }
        },
        {
          selector: '.label-hovered',
          style: {
            'text-background-color': '#f0f0f0'
          }
        }
      ],
      minZoom: 0.1,
      maxZoom: 3,
      userZoomingEnabled: !isExportMode,
      userPanningEnabled: !isExportMode,
      boxSelectionEnabled: false,
      autoungrabify: isExportMode,
      autolock: isExportMode,
      headless: isExportMode,
      selectionType: 'single',
      autounselectify: false,
      layout: {
        name: 'preset',
        fit: false,
        padding: 0
      }
    });

    const cy = cyRef.current;
    // Store project data in both data and scratch space
    cy.data('project', project);
    cy.scratch('project', project);

    // Set initial zoom and pan
    cy.zoom(1);
    cy.center();
    
    // Check if graph fits at zoom 1
    const boundingBox = cy.elements().boundingBox();
    const containerWidth = cy.width();
    const containerHeight = cy.height();
    
    // If graph doesn't fit at zoom 1, use fit
    if (boundingBox.w > containerWidth || boundingBox.h > containerHeight) {
      cy.fit(undefined, 50);
    }

    // Notify parent component of Cytoscape instance
    onCytoscapeInstance(cy);

    // Cleanup function
    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = undefined;
      }
    };
  }, [project.id]); // Reinitialize when project ID changes

  const handleRouteUpdate = (route: Route, updates: Partial<Route>) => {
    const previousRoute = currentProjectRef.current.network.routes.find(r => r.id === route.id)!;
    const updatedRoute = { ...route, ...updates };

    // Check if this is just an automatic distance update
    const isAutomaticUpdate = Object.keys(updates).length === 1 && 'automaticDistance' in updates;

    // Only update automatic distance if switching calculation method
    if (updates.distanceCalculation === 'automatic' || 
        updates.distanceCalculation === 'manual') {
      // Get the stations
      const sourceStation = currentProjectRef.current.network.stations.find(s => s.id === route.source);
      const targetStation = currentProjectRef.current.network.stations.find(s => s.id === route.target);

      // Only calculate automatic distance if both stations have coordinates
      if (sourceStation?.latitude && sourceStation?.longitude && 
          targetStation?.latitude && targetStation?.longitude) {
        const effectiveDistance = getEffectiveDistance(updatedRoute, currentProjectRef.current, true);
        updatedRoute.automaticDistance = effectiveDistance;
      } else {
        // If coordinates are missing, remove the automatic distance
        updatedRoute.automaticDistance = null;
        if (updates.distanceCalculation === 'automatic') {
          const missingCoordinates = [];
          if (!sourceStation?.latitude || !sourceStation?.longitude) {
            missingCoordinates.push(sourceStation?.name || 'unknown source station');
          }
          if (!targetStation?.latitude || !targetStation?.longitude) {
            missingCoordinates.push(targetStation?.name || 'unknown target station');
          }
          console.warn(
            `Route ${route.id} cannot use automatic distance calculation because ` +
            `${missingCoordinates.join(' and ')} ${missingCoordinates.length > 1 ? 'are' : 'is'} missing GPS coordinates.`
          );
        }
      }
    }

    // Add to undo history for calculation method changes and distance changes
    if (updates.distanceCalculation || updates.distance) {
      undoManagerRef.current?.addAction({
        type: 'UPDATE_ROUTE',
        data: {
          routeId: route.id,
          previousRoute,
          newRoute: updatedRoute
        }
      });
    }

    // Update the project
    const updatedProject = {
      ...currentProjectRef.current,
      network: {
        ...currentProjectRef.current.network,
        routes: currentProjectRef.current.network.routes.map(r =>
          r.id === route.id ? updatedRoute : r
        )
      }
    };

    // Update refs and save
    currentProjectRef.current = updatedProject;
    saveProject(updatedProject);
    onProjectChange(updatedProject, !isAutomaticUpdate);  // Don't update timestamp for automatic updates

    // Update the edge in cytoscape
    const edge = cyRef.current?.getElementById(route.id);
    if (edge) {
      edge.data('route', updatedRoute);
      edge.data('label', getEdgeLabel(updatedRoute, updatedProject));
      // Update passenger data
      const sourceIndex = updatedProject.network.stations.findIndex(s => s.id === route.source);
      const targetIndex = updatedProject.network.stations.findIndex(s => s.id === route.target);
      edge.data('passengers', updatedProject.matrices?.passengersBySegment?.[sourceIndex]?.[targetIndex] || 0);
      edge.data('passengersPerKm', updatedProject.matrices?.passengersBySegmentKm?.[sourceIndex]?.[targetIndex] || 0);
    }

    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const handleStationUpdate = (station: Station, updates: Partial<Station>) => {
    const previousStation = currentProjectRef.current.network.stations.find(s => s.id === station.id)!;
    const updatedStation = { ...station, ...updates };

    // Check if coordinates have changed
    const coordinatesChanged = 
      (updates.latitude !== undefined && updates.latitude !== previousStation.latitude) ||
      (updates.longitude !== undefined && updates.longitude !== previousStation.longitude);

    // Check if this is just an automatic population update
    const isAutomaticUpdate = Object.keys(updates).length === 1 && 'population' in updates;

    undoManagerRef.current?.addAction({
      type: 'EDIT_STATION',
      data: {
        stationId: station.id,
        previousStation,
        newStation: updatedStation
      }
    });

    const updatedProject = {
      ...currentProjectRef.current,
      network: {
        ...currentProjectRef.current.network,
        stations: currentProjectRef.current.network.stations.map(s =>
          s.id === station.id ? updatedStation : s
        )
      }
    };

    // Update automatic distances only for directly connected routes if coordinates changed
    if (coordinatesChanged) {
      updateAutomaticDistancesForStation(station.id, updatedProject);
    }

    console.log(`[Editor] Project updated with new station data`);
    currentProjectRef.current = updatedProject;
    saveProject(updatedProject);
    onProjectChange(updatedProject, !isAutomaticUpdate);  // Don't update timestamp for automatic updates

    const node = cyRef.current?.getElementById(station.id);
    if (node) {
      console.log(`[Editor] Updating node data in graph`);
      node.data('station', { ...station, ...updates });
      if (updates.name !== undefined) {
        updateNodeLabel(node, updates.name);
      }
    }

    // Update nodeContextMenu if it's open for this station
    if (nodeContextMenu?.station.id === station.id) {
      setNodeContextMenu({
        ...nodeContextMenu,
        station: updatedStation
      });
    }

    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const handleStationDelete = (station: Station) => {
    const connectedEdges = cyRef.current?.getElementById(station.id).connectedEdges();
    const connectedRoutes = connectedEdges?.map(edge => edge.data('route')) || [];

    undoManagerRef.current?.addAction({
      type: 'DELETE_STATION',
      data: {
        station,
        connectedRoutes
      }
    });

    const updatedProject = {
      ...currentProjectRef.current,
      network: {
        ...currentProjectRef.current.network,
        stations: currentProjectRef.current.network.stations.filter(s => s.id !== station.id),
        routes: currentProjectRef.current.network.routes.filter(r => !connectedRoutes.some(cr => cr.id === r.id))
      }
    };

    currentProjectRef.current = updatedProject;
    saveProject(updatedProject);
    onProjectChange(updatedProject);

    cyRef.current?.getElementById(station.id).remove();

    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const handleRouteDelete = (route: Route) => {
    undoManagerRef.current?.addAction({
      type: 'DELETE_ROUTE',
      data: {
        route
      }
    });

    const updatedProject = {
      ...currentProjectRef.current,
      network: {
        ...currentProjectRef.current.network,
        routes: currentProjectRef.current.network.routes.filter(r => r.id !== route.id)
      }
    };

    currentProjectRef.current = updatedProject;
    saveProject(updatedProject);
    onProjectChange(updatedProject);

    cyRef.current?.getElementById(route.id).remove();
    
    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const handleUndo = () => {
    const action = undoManagerRef.current?.undo();
    if (!action) {
      console.log('Nothing to undo');
      return;
    }

    let updatedProject: Project;

    switch (action.type) {
      case 'ADD_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.filter(s => s.id !== action.data.station.id)
          }
        };
        cyRef.current?.getElementById(action.data.station.id).remove();
        // Close the NodeContextMenu if it's open for the deleted station
        if (nodeContextMenu?.station.id === action.data.station.id) {
          setNodeContextMenu(null);
        }
        break;

      case 'DELETE_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: [...currentProjectRef.current.network.stations, action.data.station],
            routes: [...currentProjectRef.current.network.routes, ...action.data.connectedRoutes]
          }
        };
        // Restore station and its routes in cytoscape
        cyRef.current?.add({
          data: {
            id: action.data.station.id,
            label: action.data.station.id,
            name: action.data.station.name,
            station: action.data.station
          },
          position: action.data.station.position
        });
        action.data.connectedRoutes.forEach((route: Route) => {
          cyRef.current?.add({
            data: {
              id: route.id,
              source: route.source,
              target: route.target,
              route,
              label: getEdgeLabel(route, updatedProject),
              passengers: project.matrices?.passengersBySegment?.[
                project.network.stations.findIndex(s => s.id === route.source)
              ]?.[
                project.network.stations.findIndex(s => s.id === route.target)
              ] || 0,
              passengersPerKm: project.matrices?.passengersBySegmentKm?.[
                project.network.stations.findIndex(s => s.id === route.source)
              ]?.[
                project.network.stations.findIndex(s => s.id === route.target)
              ] || 0
            }
          });
        });
        break;

      case 'EDIT_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.map(s =>
              s.id === action.data.stationId ? action.data.previousStation : s
            )
          }
        };
        // Update station in cytoscape
        const node = cyRef.current?.getElementById(action.data.stationId);
        if (node) {
          node.data('name', action.data.previousStation.name);
          node.data('station', action.data.previousStation);
        }
        break;

      case 'ADD_ROUTE':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: currentProjectRef.current.network.routes.filter(r => r.id !== action.data.route.id)
          }
        };
        cyRef.current?.getElementById(action.data.route.id).remove();
        break;

      case 'DELETE_ROUTE':
        console.log('Restoring deleted route:', action.data.route);
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: [...currentProjectRef.current.network.routes, action.data.route]
          }
        };
        // Restore the route in cytoscape
        cyRef.current?.add({
          data: {
            id: action.data.route.id,
            source: action.data.route.source,
            target: action.data.route.target,
            route: action.data.route,
            label: getEdgeLabel(action.data.route, updatedProject),
            passengers: project.matrices?.passengersBySegment?.[
              project.network.stations.findIndex(s => s.id === action.data.route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === action.data.route.target)
            ] || 0,
            passengersPerKm: project.matrices?.passengersBySegmentKm?.[
              project.network.stations.findIndex(s => s.id === action.data.route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === action.data.route.target)
            ] || 0
          }
        });
        break;

      case 'MOVE_STATION':
        console.log('Undoing move:', {
          stationId: action.data.stationId,
          from: action.data.newPosition,
          to: action.data.previousPosition
        });
        
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.map(s =>
              s.id === action.data.stationId
                ? { ...s, position: action.data.previousPosition }
                : s
            )
          }
        };

        currentProjectRef.current = updatedProject;
        
        // Update station position in cytoscape
        const movedNode = cyRef.current?.getElementById(action.data.stationId);
        if (movedNode) {
          movedNode.position(action.data.previousPosition);
        }
        break;

      case 'EDIT_DISTANCE':
      case 'UPDATE_ROUTE':
        if ('landType' in action.data.previousRoute && 'landType' in action.data.newRoute) {
          console.log('Undoing route landType change:', {
            routeId: action.data.routeId,
            from: action.data.newRoute.landType,
            to: action.data.previousRoute.landType
          });
        }
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: currentProjectRef.current.network.routes.map(r =>
              r.id === action.data.routeId ? action.data.previousRoute : r
            )
          }
        };
        // Update route in cytoscape
        const edge = cyRef.current?.getElementById(action.data.routeId);
        if (edge) {
          edge.data('route', action.data.previousRoute);
          edge.data('label', getEdgeLabel(action.data.previousRoute, updatedProject));
        }
        break;

      default:
        console.warn('Unknown action type:', action.type);
        return;
    }

    onProjectChange(updatedProject);
    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const handleRedo = () => {
    const action = undoManagerRef.current?.redo();
    if (!action) {
      console.log('Nothing to redo');
      return;
    }

    let updatedProject: Project;

    switch (action.type) {
      case 'ADD_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: [...currentProjectRef.current.network.stations, action.data.station]
          }
        };
        cyRef.current?.add({
          data: {
            id: action.data.station.id,
            label: action.data.station.id,
            name: action.data.station.name,
            station: action.data.station
          },
          position: action.data.station.position
        });
        break;

      case 'DELETE_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.filter(s => s.id !== action.data.station.id),
            routes: currentProjectRef.current.network.routes.filter(r => !action.data.connectedRoutes.some((cr: Route) => cr.id === r.id))
          }
        };
        cyRef.current?.getElementById(action.data.station.id).remove();
        break;

      case 'EDIT_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.map(s =>
              s.id === action.data.stationId ? action.data.newStation : s
            )
          }
        };
        const node = cyRef.current?.getElementById(action.data.stationId);
        if (node) {
          node.data('name', action.data.newStation.name);
          node.data('station', action.data.newStation);
        }
        break;

      case 'ADD_ROUTE':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: [...currentProjectRef.current.network.routes, action.data.route]
          }
        };
        cyRef.current?.add({
          data: {
            id: action.data.route.id,
            source: action.data.route.source,
            target: action.data.route.target,
            route: action.data.route,
            label: getEdgeLabel(action.data.route, updatedProject),
            passengers: project.matrices?.passengersBySegment?.[
              project.network.stations.findIndex(s => s.id === action.data.route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === action.data.route.target)
            ] || 0,
            passengersPerKm: project.matrices?.passengersBySegmentKm?.[
              project.network.stations.findIndex(s => s.id === action.data.route.source)
            ]?.[
              project.network.stations.findIndex(s => s.id === action.data.route.target)
            ] || 0
          }
        });
        break;

      case 'DELETE_ROUTE':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: currentProjectRef.current.network.routes.filter(r => r.id !== action.data.route.id)
          }
        };
        cyRef.current?.getElementById(action.data.route.id).remove();
        break;

      case 'MOVE_STATION':
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            stations: currentProjectRef.current.network.stations.map(s =>
              s.id === action.data.stationId
                ? { ...s, position: action.data.newPosition }
                : s
            )
          }
        };
        // Update station position in cytoscape
        const movedNode = cyRef.current?.getElementById(action.data.stationId);
        if (movedNode) {
          movedNode.position(action.data.newPosition);
        }
        break;

      case 'EDIT_DISTANCE':
      case 'UPDATE_ROUTE':
        if ('landType' in action.data.previousRoute && 'landType' in action.data.newRoute) {
          console.log('Redoing route landType change:', {
            routeId: action.data.routeId,
            from: action.data.previousRoute.landType,
            to: action.data.newRoute.landType
          });
        }
        updatedProject = {
          ...currentProjectRef.current,
          network: {
            ...currentProjectRef.current.network,
            routes: currentProjectRef.current.network.routes.map(r =>
              r.id === action.data.routeId ? action.data.newRoute : r
            )
          }
        };
        const edge = cyRef.current?.getElementById(action.data.routeId);
        if (edge) {
          edge.data('route', action.data.newRoute);
          edge.data('label', getEdgeLabel(action.data.newRoute, updatedProject));
        }
        break;

      default:
        console.warn('Unknown action type:', action.type);
        return;
    }

    onProjectChange(updatedProject);
    setCanUndo(undoManagerRef.current?.canUndo() || false);
    setCanRedo(undoManagerRef.current?.canRedo() || false);
  };

  const createNewRoute = (source: string, target: string): Route => {
    const newRoute: Route = {
      id: getNextRouteId(currentProjectRef.current.network.routes),
      source,
      target,
      distance: 0,
      landType: 'domestic' as const,
      distanceCalculation: 'manual',
      automaticDistance: null
    };
    console.log('Creating new route object:', newRoute);
    return newRoute;
  };

  const saveViewState = (state: ViewState) => {
    const newProject = {
      ...project,
      viewState: state
    };
    onProjectChange(newProject, false);  // Don't update timestamp for view state changes
  };

  const handleSelectAllNodes = () => {
    if (!cyRef.current) return;
    
    const allNodes = cyRef.current.nodes();
    allNodes.forEach(node => {
      node.addClass('selected');
    });
    
    selectionStateRef.current = {
      selectedNodes: allNodes.toArray() as NodeSingular[],
      routeCreationSource: null
    };
    
    setSelectionState(selectionStateRef.current);
  };

  // Update edge data when matrices become available
  useEffect(() => {
    if (!cyRef.current || !project.matrices) return;

    // Update all edges with current passenger data
    cyRef.current.edges().forEach((edge: EdgeSingular) => {
      const route = edge.data('route');
      const sourceIndex = project.network.stations.findIndex(s => s.id === route.source);
      const targetIndex = project.network.stations.findIndex(s => s.id === route.target);
      
      edge.data('passengers', project.matrices?.passengersBySegment?.[sourceIndex]?.[targetIndex] || 0);
      edge.data('passengersPerKm', project.matrices?.passengersBySegmentKm?.[sourceIndex]?.[targetIndex] || 0);
    });
  }, [project.matrices]); // Only re-run when matrices change

  // Update cytoscape instance when project changes
  useEffect(() => {
    if (!cyRef.current) {
      console.warn('Cytoscape instance not available');
      return;
    }

    console.log('Updating cytoscape instance for project:', {
      projectId: project.id,
      stationCount: project.network.stations.length,
      routeCount: project.network.routes.length,
      previousProjectId
    });

    try {
      const cy = cyRef.current;
      if (!cy) {
        console.warn('Cytoscape instance is null');
        return;
      }

      // Update nodes
      const existingNodes = new Set(cy.nodes().map(n => n.id()));
      const existingEdges = new Set(cy.edges().map(e => e.id()));

      // Remove nodes that no longer exist in project
      cy.nodes().forEach(node => {
        if (!project.network.stations.some(s => s.id === node.id())) {
          node.remove();
        }
      });

      // Add or update nodes
      project.network.stations.forEach(station => {
        if (existingNodes.has(station.id)) {
          // Update existing node
          const node = cy.getElementById(station.id);
          if (node) {
            updateNodeLabel(node, station.name);
            node.data('station', station);
            node.position(station.position);
          }
        } else {
          // Add new node
          cy.add({
            data: {
              id: station.id,
              label: station.name,
              name: station.name,
              station: station
            },
            position: station.position
          });
        }
      });

      // Remove edges that no longer exist in project
      cy.edges().forEach(edge => {
        if (!project.network.routes.some(r => r.id === edge.id())) {
          edge.remove();
        }
      });

      // Add or update edges
      project.network.routes.forEach(route => {
        if (existingEdges.has(route.id)) {
          // Update existing edge
          const edge = cy.getElementById(route.id);
          if (edge) {
            edge.data('route', route);
            edge.data('label', getEdgeLabel(route, project));
          }
        } else {
          // Add new edge
          cy.add({
            data: {
              id: route.id,
              source: route.source,
              target: route.target,
              route: route,
              label: getEdgeLabel(route, project)
            }
          });
        }
      });

      // Center and fit on initial load, when switching projects, or when explicitly requested
      const isInitialLoad = !existingNodes.size && !existingEdges.size;
      const isProjectSwitch = previousProjectId !== project.id;
      
      if (isInitialLoad || isProjectSwitch) {
        console.log('Centering and fitting graph:', {
          isInitialLoad,
          isProjectSwitch,
          previousProjectId,
          currentProjectId: project.id
        });
        
        // Ensure cytoscape instance exists and is valid
        if (!cy || !cy.container()) {
          console.warn('Cytoscape instance not available or invalid');
          return;
        }

        // Ensure the graph has elements before centering
        const nodes = cy.nodes();
        if (nodes.length > 0) {
          try {
            // Check if cytoscape instance is still valid before operations
            if (!cy || !cy.container()) {
              console.warn('Cytoscape instance became invalid during centering');
              return;
            }

            cy.zoom(1);
            cy.center();
            
            // Check if cytoscape instance is still valid before getting dimensions
            if (!cy || !cy.container()) {
              console.warn('Cytoscape instance became invalid during dimension checks');
              return;
            }

            // Check if graph fits at zoom 1
            const initialBoundingBox = cy.elements().boundingBox();
            const initialContainerWidth = cy.width();
            const initialContainerHeight = cy.height();
            
            // If graph doesn't fit at zoom 1, use fit
            if (initialBoundingBox.w > initialContainerWidth || initialBoundingBox.h > initialContainerHeight) {
              cy.fit(undefined, 50);
            }
          } catch (error) {
            console.warn('Error centering/fitting graph:', error);
          }
        }
      }

      // Update previous project ID
      setPreviousProjectId(project.id);
    } catch (error) {
      console.error('Error updating Cytoscape instance:', error);
    }
  }, [project.network, project.id]); // Re-run when network or project ID changes

  // Add event handlers
  useEffect(() => {
    if (!cyRef.current) return;

    const cy = cyRef.current;
    console.log('Registering event handlers for Cytoscape instance');

    // Add hover handlers for node labels
    cy.on('mouseover', 'node[label]', (evt) => {
      const node = evt.target;
      node.style('text-background-color', '#f0f0f0');
    });

    cy.on('mouseout', 'node[label]', (evt) => {
      const node = evt.target;
      node.style('text-background-color', '#ffffff');
    });

    // Add hover handlers for edge labels
    cy.on('mouseover', 'edge[label]', (evt) => {
      const edge = evt.target;
      edge.style('text-background-color', '#f0f0f0');
    });

    cy.on('mouseout', 'edge[label]', (evt) => {
      const edge = evt.target;
      edge.style('text-background-color', '#ffffff');
    });

    // Add click handler for edge labels
    cy.on('tap', 'edge[label]', (evt) => {
      const edge = evt.target;
      const route = edge.data('route');
      
      // Get the rendered position of the click
      const clickPos = evt.renderedPosition;
      
      // Position menu 40px to the right of the click position
      setContextMenu({
        route,
        position: { x: clickPos.x + 40, y: clickPos.y }
      });
    });

    // Add drag event handlers
    cy.on('grab', 'node', (evt) => {
      const node = evt.target;
      console.log('Node grabbed:', node.data('name'));
      // Set initial position when node is grabbed
      dragStartPositionRef.current = [{
        id: node.id(),
        x: node.position().x,
        y: node.position().y
      }];
      
      // If there are other selected nodes, store their positions too
      if (node.selected()) {
        const selectedNodes = cy.nodes(':selected').filter(n => n.id() !== node.id());
        dragStartPositionRef.current = [
          ...dragStartPositionRef.current,
          ...selectedNodes.map((n: NodeSingular) => ({
            id: n.id(),
            x: n.position().x,
            y: n.position().y
          }))
        ];
      }
    });

    // Add tap handler for node creation
    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        const pos = evt.position;
        
        stationCreationServiceRef.current?.createStation(pos)
          .then(newStation => {
            undoManagerRef.current?.addAction({
              type: 'ADD_STATION',
              data: {
                station: newStation
              }
            });

            const newNode = cy.getElementById(newStation.id);
            handleNodeSelection(newNode);

            // Use the helper function for consistent positioning
            const menuPosition = calculateMenuPosition(newNode);
            setNodeContextMenu({
              station: newStation,
              position: menuPosition
            });
          })
          .catch(error => {
            console.error('Failed to create station:', error);
          });
      }
    });

    // Add tap handler for node selection
    cy.on('tap', 'node', (evt) => {
      const node = evt.target;
      const station = node.data('station');
      
      // Get the rendered position of the click
      const clickPos = evt.renderedPosition;
      // Get the node's rendered position
      const nodePos = node.renderedPosition();
      // Get the node's dimensions
      const nodeWidth = node.width();
      const nodeHeight = node.height();
      
      // Calculate the distance from click to node center
      const dx = clickPos.x - nodePos.x;
      const dy = clickPos.y - nodePos.y;
      
      // If click is outside the node's main body (circle), it's likely on the label
      const isLabelClick = Math.sqrt(dx * dx + dy * dy) > Math.min(nodeWidth, nodeHeight) / 2;
      
      if (isLabelClick) {
        // Use the helper function for consistent positioning
        const menuPosition = calculateMenuPosition(node);
        setNodeContextMenu({
          station,
          position: menuPosition
        });
      } else {
        // Handle node selection and route creation
        const currentSelection = selectionStateRef.current;
        
        if (currentSelection.routeCreationSource && 
            currentSelection.routeCreationSource.id() !== node.id() &&
            currentSelection.routeCreationSource.selected()) {  // Check if source node is still selected
          // If we have a source node and clicked a different node, create a route
          handleRouteCreation(currentSelection.routeCreationSource, node);
        }
        
        // Always select the clicked node for potential next route
        handleNodeSelection(node);
      }
    });

    // Cleanup function
    return () => {
      if (cyRef.current) {
        cyRef.current.removeAllListeners();
      }
    };
  }, [project.id]);

  // Initialize keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Handle keyboard shortcuts
      switch (event.key.toLowerCase()) {
        case 'escape':
          // Clear selection and close all menus
          handleNodeSelection(null);
          setShowRoutesMenu(false);
          setShowCitiesMenu(false);
          setShowInfoScreen(false);
          setShowConfigForm(false);
          setContextMenu(null);
          setNodeContextMenu(null);
          break;
        case 'z':
          // Only allow undo/redo when no menus are open
          if (showRoutesMenu || showCitiesMenu || showInfoScreen || showConfigForm || contextMenu || nodeContextMenu) {
            return;
          }
          // Undo/Redo
          if ((event.metaKey || event.ctrlKey) && event.shiftKey) {
            // Cmd/Ctrl + Shift + Z: Redo
            handleRedo();
          } else if (event.metaKey || event.ctrlKey) {
            // Cmd/Ctrl + Z: Undo
            handleUndo();
          }
          break;
        case 'h':
          // Only trigger if cmd/ctrl is not pressed
          if (!(event.metaKey || event.ctrlKey)) {
            // Close other menus and open help screen
            setShowRoutesMenu(false);
            setShowCitiesMenu(false);
            setShowConfigForm(false);
            setContextMenu(null);
            setNodeContextMenu(null);
            setShowInfoScreen(true);
          }
          break;
        case 'f':
          // Only trigger if cmd/ctrl is not pressed
          if (!(event.metaKey || event.ctrlKey)) {
            // Only allow fit and center when no menus are open
            if (showRoutesMenu || showCitiesMenu || showInfoScreen || showConfigForm || contextMenu || nodeContextMenu) {
              return;
            }
            // Fit and center
            if (cyRef.current) {
              const cy = cyRef.current;
              cy.zoom(1);
              cy.center();
              
              // Check if graph fits at zoom 1
              const boundingBox = cy.elements().boundingBox();
              const containerWidth = cy.width();
              const containerHeight = cy.height();
              
              // If graph doesn't fit at zoom 1, use fit
              if (boundingBox.w > containerWidth || boundingBox.h > containerHeight) {
                cy.fit(undefined, 50);
              }
            }
          }
          break;
        case 'r':
          // Only trigger if cmd/ctrl is not pressed
          if (!(event.metaKey || event.ctrlKey)) {
            // Close other menus and open routes menu
            setShowCitiesMenu(false);
            setShowInfoScreen(false);
            setShowConfigForm(false);
            setContextMenu(null);
            setNodeContextMenu(null);
            setShowRoutesMenu(true);
          }
          break;
        case 'c':
          // Only trigger if cmd/ctrl is not pressed
          if (!(event.metaKey || event.ctrlKey)) {
            // Close other menus and open cities menu
            setShowRoutesMenu(false);
            setShowInfoScreen(false);
            setShowConfigForm(false);
            setContextMenu(null);
            setNodeContextMenu(null);
            setShowCitiesMenu(true);
          }
          break;
      }
    };

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showRoutesMenu, showCitiesMenu, showInfoScreen, showConfigForm, contextMenu, nodeContextMenu]);

  return (
    <div className={`relative w-full h-full ${className || ''}`} style={isExportMode ? { display: 'none' } : undefined}>
      <div className="editor-container h-full">
        <div
          ref={containerRef}
          className="w-full h-full editor-canvas"
        />
      </div>
      <div className="absolute left-0 right-0" style={{ bottom: '32px' }}>
        <div className="flex editor-menu gap-2 px-4 pointer-events-auto">
          <div className="flex justify-center sm:justify-start">
            <div className="flex gap-2">
              <button
                onClick={() => setShowInfoScreen(true)}
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-500"
                >
                  <path
                    d="m17 21-5-4-5 4V3.889a.92.92 0 0 1 .244-.629.808.808 0 0 1 .59-.26h8.333a.81.81 0 0 1 .589.26.92.92 0 0 1 .244.63V21Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">Help</span>
              </button>
              <UndoRedoControls
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
              />
              <button
                onClick={() => {
                  if (cyRef.current) {
                    const cy = cyRef.current;
                    cy.zoom(1);
                    cy.center();
                    
                    // Check if graph fits at zoom 1
                    const boundingBox = cy.elements().boundingBox();
                    const containerWidth = cy.width();
                    const containerHeight = cy.height();
                    
                    // If graph doesn't fit at zoom 1, use fit
                    if (boundingBox.w > containerWidth || boundingBox.h > containerHeight) {
                      cy.fit(undefined, 50);
                    }
                  }
                }}
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors fit-button"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-500"
                >
                  <path
                    d="M6 6h3m6 0h3M6 6v3m0 6v3M6 18h3m6 0h3M18 6v3m0 6v3"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">Fit and center</span>
              </button>
              <button
                onClick={() => setShowRoutesMenu(true)}
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors routes-cities-buttons"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-500"
                >
                  <path d="M8 3a3 3 0 0 0-1 5.83v6.34a3.001 3.001 0 1 0 2 0V15a2 2 0 0 1 2-2h1a5.002 5.002 0 0 0 4.927-4.146A3.001 3.001 0 0 0 16 3a3 3 0 0 0-1.105 5.79A3.001 3.001 0 0 1 12 11h-1c-.729 0-1.412.195-2 .535V8.83A3.001 3.001 0 0 0 8 3Z"/>
                </svg>
                <span className="text-sm text-gray-600">Routes</span>
              </button>
              <button
                onClick={() => setShowCitiesMenu(true)}
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors routes-cities-buttons"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-500"
                >
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"
                    fill="currentColor"
                  />
                </svg>
                <span className="text-sm text-gray-600">Cities</span>
              </button>
            </div>
          </div>
          <div className="flex justify-center sm:justify-end">
            <div className="flex gap-2">
              <div
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onClick={() => {
                  if (project.identifier) {
                    const newUrl = `/project/${project.identifier}/summary`;
                    window.history.pushState({}, '', newUrl);
                    window.dispatchEvent(new Event('popstate'));
                  }
                }}
              >
                <span className="text-sm text-gray-600">
                  Annual passengers: {project.matrices ? formatNumber(calculateTotalRidership(project.matrices)) : '0'}
                </span>
              </div>
              <div
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 hidden xl:flex"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onClick={() => {
                  if (project.identifier) {
                    const newUrl = `/project/${project.identifier}/config`;
                    window.history.pushState({}, '', newUrl);
                    window.dispatchEvent(new Event('popstate'));
                  }
                }}
              >
                <span className="text-sm text-gray-600">
                  Cost per km: {formatCurrencyCompact(project.config.costs.constructionCostPerKm, project.config.bond.currency)}
                </span>
              </div>
              <div
                className="h-8 px-3 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 net-income-display"
                style={{
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onClick={() => {
                  if (project.identifier) {
                    const newUrl = `/project/${project.identifier}/summary`;
                    window.history.pushState({}, '', newUrl);
                    window.dispatchEvent(new Event('popstate'));
                  }
                }}
              >
                <span className="text-sm text-gray-600">
                  Net income:{' '}
                  {project.matrices ? (
                    <span className={getPercentageColor(calculateNetIncomePercentage(
                      calculateRevenue(project.matrices),
                      calculateNetIncome(project)
                    ))}>
                      {formatPercentage(calculateNetIncomePercentage(
                        calculateRevenue(project.matrices),
                        calculateNetIncome(project)
                      ))}
                    </span>
                  ) : '0%'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {contextMenu && (
        <div
          onClick={(e) => {
            setContextMenu(null);
            setNodeContextMenu(null);
          }}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            pointerEvents: 'all',
            zIndex: 1000
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', left: contextMenu.position.x, top: contextMenu.position.y, zIndex: 1001 }}>
            <EdgeContextMenu
              route={contextMenu.route}
              project={currentProjectRef.current}
              position={contextMenu.position}
              onClose={() => setContextMenu(null)}
              onUpdate={(updates) => {
                handleRouteUpdate(contextMenu.route, updates);
              }}
              onDelete={() => {
                handleRouteDelete(contextMenu.route);
                setContextMenu(null);
              }}
            />
          </div>
        </div>
      )}
      {nodeContextMenu && (
        <div
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            pointerEvents: 'all',
            zIndex: 1000
          }}
        >
          <div style={{ position: 'absolute', left: nodeContextMenu.position.x, top: nodeContextMenu.position.y, zIndex: 1001 }}>
            <NodeContextMenu
              station={nodeContextMenu.station}
              position={nodeContextMenu.position}
              onClose={() => {
                // Deselect the node when closing the context menu
                const node = cyRef.current?.getElementById(nodeContextMenu.station.id);
                if (node) {
                  node.unselect();
                }
                setNodeContextMenu(null);
              }}
              onUpdate={(updates) => {
                handleStationUpdate(nodeContextMenu.station, updates);
              }}
              onDelete={() => {
                handleStationDelete(nodeContextMenu.station);
                setNodeContextMenu(null);
              }}
              config={currentProjectRef.current.config}
              project={currentProjectRef.current}
            />
          </div>
        </div>
      )}
      {showRoutesMenu && (
        <RoutesMenu
          project={currentProjectRef.current}
          isOpen={showRoutesMenu}
          onClose={handleCloseRoutesMenu}
          onUpdate={handleRouteUpdate}
        />
      )}
      {showInfoScreen && (
        <InfoScreen
          onClose={handleCloseInfoScreen}
          project={currentProjectRef.current}
          isOpen={showInfoScreen}
        />
      )}
      {showCitiesMenu && (
        <CitiesMenu
          stations={currentProjectRef.current.network.stations}
          onUpdate={(stationId, updates) => {
            const station = currentProjectRef.current.network.stations.find(s => s.id === stationId);
            if (station) {
              handleStationUpdate(station, updates);
            }
          }}
          onClose={handleCloseCitiesMenu}
          isOpen={showCitiesMenu}
          project={currentProjectRef.current}
        />
      )}
    </div>
  );
}; 